import React from 'react';
import TransactionalSheet from './TransactionalSheet';
import { DotsLoader } from './SecureInputStage';
import {
  AlertTriangleIcon,
  CheckIcon,
  CreditCardLockIcon,
  LinkBrokenIcon,
  MessageIcon,
  XCircleIcon,
} from './transactional-icons';
import {
  FlightArtifactBookingState,
  FlightOfferSlice,
  formatClock,
  formatFlightDate,
  formatMoney,
  passengerName,
  userFacingDetail,
} from './flightArtifact';
import './secure-input-sheet.scss';

const STAGE_ICON_SIZE = 32;
const STAGE_ICON_STROKE = (2 * 24) / STAGE_ICON_SIZE;

export interface FlightBookingProgressSummary {
  route: string;
  airline: string;
  fareBrand: string;
  passengers: string[];
  total: number;
  currency: string;
}

interface FlightBookingProgressSheetProps {
  show: boolean;
  /** Null while the Book request itself is still in flight. */
  state: FlightArtifactBookingState | null;
  summary: FlightBookingProgressSummary;
  assistantName: string;
  /** Where the assistant will follow up once a parked payment completes. */
  notifyVia?: 'chat' | 'whatsapp';
  onDone: () => void;
}

type Stage = {
  kind: 'booking' | 'confirmed' | 'pending' | 'changed' | 'failed' | 'unknown';
  title: string;
  body: React.ReactNode;
  busy?: boolean;
};

const STAGE_ICONS: Record<
  Stage['kind'],
  React.FC<{ size?: number; strokeWidth?: number }>
> = {
  booking: CreditCardLockIcon,
  confirmed: CheckIcon,
  pending: MessageIcon,
  changed: AlertTriangleIcon,
  failed: XCircleIcon,
  unknown: LinkBrokenIcon,
};

function sliceLine(slice: FlightOfferSlice): string {
  return `${slice.origin} ${formatClock(slice.departingAt)} → ${slice.destination} ${formatClock(slice.arrivingAt)}, ${formatFlightDate(slice.departingAt)}`;
}

function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

function describe(
  state: FlightArtifactBookingState | null,
  summary: FlightBookingProgressSummary,
  assistant: string,
  notifyVia: 'chat' | 'whatsapp',
): Stage {
  if (!state || state.state === 'booking') {
    return {
      kind: 'booking',
      title: 'Booking your flight',
      body: (
        <p>
          Confirming the {summary.fareBrand} fare on {summary.route} with{' '}
          {summary.airline} for {joinNames(summary.passengers)} at{' '}
          {formatMoney(summary.total, summary.currency)}. This usually takes a
          few minutes.
        </p>
      ),
      busy: true,
    };
  }
  if (state.state === 'booked') {
    const who = joinNames(state.passengers.map(passengerName));
    const airline = state.airlineNames.join(' · ') || summary.airline;
    const flights = state.slices.map(sliceLine);
    if (state.status === 'confirmed') {
      return {
        kind: 'confirmed',
        title: 'Booked',
        body: (
          <>
            <p>
              {airline}, {state.fareBrand}, for {who}. Charged{' '}
              {formatMoney(state.total, state.currency)}.
            </p>
            {flights.map((line) => (
              <p key={line}>{line}</p>
            ))}
            {state.bookingReference && (
              <p>
                Booking reference: <b>{state.bookingReference}</b>
              </p>
            )}
            {state.ticketNumbers.length > 0 && (
              <p className='fine'>
                Ticket{state.ticketNumbers.length === 1 ? '' : 's'}:{' '}
                {state.ticketNumbers.join(', ')}
              </p>
            )}
            <p className='fine'>{state.conditions}</p>
            <p className='fine'>
              Any changes or cancellation, just message me — I'll handle it.
            </p>
          </>
        ),
      };
    }
    return {
      kind: 'pending',
      title: 'Booked, tickets on their way',
      body: (
        <>
          <p>
            {airline}, {state.fareBrand}, for {who}, at{' '}
            {formatMoney(state.total, state.currency)}. The airline is issuing
            the tickets — {assistant} will send them on as soon as they arrive.
          </p>
          {flights.map((line) => (
            <p key={line}>{line}</p>
          ))}
          {state.bookingReference && (
            <p>
              Booking reference: <b>{state.bookingReference}</b>
            </p>
          )}
          <p className='fine'>{state.conditions}</p>
        </>
      ),
    };
  }
  if (state.state === 'awaiting_payment') {
    const channel = notifyVia === 'whatsapp' ? 'via WhatsApp' : 'here in chat';
    return {
      kind: 'pending',
      title: 'Confirming your booking',
      body: (
        <>
          <p>
            {assistant} is confirming your {summary.route} flight with{' '}
            {summary.airline} for {joinNames(summary.passengers)} at{' '}
            {formatMoney(state.total, state.currency)}, and will let you know{' '}
            {channel} when it's booked.
          </p>
          {state.heldBookingReference && (
            <p className='fine'>
              The airline is holding your seats at this price meanwhile
              (reference {state.heldBookingReference}).
            </p>
          )}
        </>
      ),
    };
  }
  if (state.state === 'unknown') {
    return {
      kind: 'unknown',
      title: "We didn't get an answer from the airline",
      body: (
        <p>
          The booking may or may not have gone through, so nothing has been
          retried. {assistant} is checking with the airline and will confirm
          either way
          {state.bookingId ? ` (reference ${state.bookingId})` : ''}.
        </p>
      ),
    };
  }
  switch (state.reason) {
    case 'price_changed':
    case 'terms_changed':
      return {
        kind: 'changed',
        title:
          state.reason === 'price_changed'
            ? 'The price has changed'
            : 'The fare conditions have changed',
        body: (
          <>
            <p>
              Nothing was booked or charged.{' '}
              {state.current
                ? `This fare is now ${formatMoney(state.current.total, state.current.currency)} — ${state.current.conditions}`
                : 'The airline returned different conditions for this fare.'}
            </p>
            <p className='fine'>
              Tell {assistant} if you'd like to go ahead at the new price, or
              pick another fare.
            </p>
          </>
        ),
      };
    case 'offer_expired':
      return {
        kind: 'changed',
        title: 'This fare is no longer available',
        body: (
          <p>
            Nothing was booked or charged. Ask {assistant} to check what the
            airline has now.
          </p>
        ),
      };
    case 'already_booked':
      return {
        kind: 'confirmed',
        title: 'Already booked',
        body: <p>This fare was booked earlier — nothing more was charged.</p>,
      };
    case 'missing_passenger_details':
    case 'invalid_passenger_details':
      return {
        kind: 'failed',
        title: 'Passenger details needed',
        body: (
          <p>
            Nothing was booked or charged.{' '}
            {userFacingDetail(state) ??
              'The airline needs each passenger’s name, date of birth and contact details exactly as on their ID.'}{' '}
            Press Book again to correct them.
          </p>
        ),
      };
    case 'insufficient_balance':
      return {
        kind: 'failed',
        title: 'Not enough balance',
        body: (
          <p>
            Nothing was booked or charged. Top up and {assistant} can book this
            fare again.
          </p>
        ),
      };
    default:
      return {
        kind: 'failed',
        title: "The airline couldn't take this booking",
        body: (
          <p>
            {[
              'Nothing was charged.',
              userFacingDetail(state),
              `${assistant} can try again or find you something else.`,
            ]
              .filter(Boolean)
              .join(' ')}
          </p>
        ),
      };
  }
}

/**
 * Replaces the confirm-spend sheet after a flight Book press: a loader while
 * the system checks and books the exact fare, then the typed outcome in
 * place. Held open until the state settles.
 */
const FlightBookingProgressSheet: React.FC<FlightBookingProgressSheetProps> = ({
  show,
  state,
  summary,
  assistantName,
  notifyVia = 'chat',
  onDone,
}) => {
  const stage = describe(state, summary, assistantName, notifyVia);
  const Icon = STAGE_ICONS[stage.kind];
  const settled = !stage.busy;
  return (
    <TransactionalSheet
      show={show}
      onClose={settled ? onDone : undefined}
      holdOpen={!settled}
      leading={<Icon size={STAGE_ICON_SIZE} strokeWidth={STAGE_ICON_STROKE} />}
      ariaLabelledBy='flight-booking-title'
      className='flight-booking-sheet'
      footer={
        settled ? (
          <button className='tx-btn-primary' type='button' onClick={onDone}>
            Done
          </button>
        ) : undefined
      }
    >
      <div
        key={stage.kind}
        className={`stage stage--${stage.kind}`}
        data-testid={`flight-booking-stage-${stage.kind}`}
      >
        <div className='stage-text'>
          <h2 className='tx-title hero' id='flight-booking-title'>
            {stage.title}
          </h2>
          {stage.body}
        </div>
        {stage.busy && (
          <div className='stage-wait'>
            <DotsLoader />
          </div>
        )}
      </div>
    </TransactionalSheet>
  );
};

export default FlightBookingProgressSheet;
